import { u8, u16 } from '../models/core/int.js';
import { AsciiString20, AsciiString120 } from '../models/core/string.js';
import {
  Artist, ArtistList, Brand, BrandList, Event, EventList, KeywordList,
  KeywordRef, Organizer, OrganizerList, SearchSnapshot, SnapshotElement,
} from '../models/serializable/search-snapshot.js';
import { BinaryPacker } from './binary-packer.js';
import { BinaryUnpacker } from './binary-unpacker.js';

export class SearchSnapshotSerializer {
  static serialize(searchSnapshot: SearchSnapshot): Uint8Array {
    const packer = new SearchSnapshotPacker(searchSnapshot);

    return packer.pack();
  }

  static deserialize(bytes: Uint8Array): SearchSnapshot {
    const unpacker = new SearchSnapshotUnpacker(bytes);

    return unpacker.unpack();
  }
}

class SearchSnapshotPacker {
  private readonly packer: BinaryPacker;

  constructor(
    private readonly searchSnapshot: SearchSnapshot,
  ) {
    this.packer = new BinaryPacker();
  }

  pack(): Uint8Array {
    this.packer.packByte(this.searchSnapshot.$version.asNumber());

    this.packer.packLength(this.searchSnapshot.length.asNumber());

    for(const element of this.searchSnapshot.elements) {
      this.packElement(element);
    }

    return this.packer.takeBytes();
  }

  private packElement(element: SnapshotElement): void {
    const typeId = element.$typeId;

    this.packer.packByte(typeId.asNumber());

    switch(true) {
      case element instanceof KeywordList: {
        this.packKeywordList(element);
      } break;
      case element instanceof EventList: {
        this.packEventList(element);
      } break;
      case element instanceof ArtistList: {
        this.packArtistList(element);
      } break;
      case element instanceof BrandList: {
        this.packBrandList(element);
      } break;
      case element instanceof OrganizerList: {
        this.packOrganizerList(element);
      } break;
      default:
        throw new Error(`Unsupported snapshot element type {${element.constructor.name ?? typeof element}}.`);
    }
  }

  private packKeywordList(keywordList: KeywordList): void {
    this.packer.packByte(keywordList.listId.asNumber());
    this.packer.packLength(keywordList.length.asNumber());

    for(const keyword of keywordList.keywords) {
      this.packer.packString(keyword.asString());
    }
  }

  private packEventList(eventList: EventList): void {
    this.packer.packLength(eventList.length.asNumber());

    for(const event of eventList.events) {
      this.packEvent(event);
    }
  }

  private packEvent(event: Event): void {
    this.packer.packString(event.uniqueId.asString());

    this.packer.packLength(event.length.asNumber());
    for(const keywordRef of event.keywords) {
      this.packKeywordRef(keywordRef);
    }
  }

  private packArtistList(artistList: ArtistList): void {
    this.packer.packLength(artistList.length.asNumber());

    for(const artist of artistList.artists) {
      this.packArtist(artist);
    }
  }

  private packArtist(artist: Artist): void {
    this.packer.packString(artist.uniqueId.asString());

    this.packer.packLength(artist.length.asNumber());
    for(const keywordRef of artist.keywords) {
      this.packKeywordRef(keywordRef);
    }
  }

  private packBrandList(brandList: BrandList): void {
    this.packer.packLength(brandList.length.asNumber());

    for(const brand of brandList.brands) {
      this.packBrand(brand);
    }
  }

  private packBrand(brand: Brand): void {
    this.packer.packString(brand.uniqueId.asString());

    this.packer.packLength(brand.length.asNumber());
    for(const keywordRef of brand.keywords) {
      this.packKeywordRef(keywordRef);
    }
  }

  private packOrganizerList(organizerList: OrganizerList): void {
    this.packer.packLength(organizerList.length.asNumber());

    for(const organizer of organizerList.organizers) {
      this.packOrganizer(organizer);
    }
  }

  private packOrganizer(organizer: Organizer): void {
    this.packer.packString(organizer.uniqueId.asString());

    this.packer.packLength(organizer.length.asNumber());
    for(const keywordRef of organizer.keywords) {
      this.packKeywordRef(keywordRef);
    }
  }

  private packKeywordRef(keywordRef: KeywordRef): void {
    this.packer.packByte(keywordRef.listId.asNumber());
    this.packer.packInt(keywordRef.index.asNumber());
  }
}

class SearchSnapshotUnpacker {
  private readonly unpacker: BinaryUnpacker;

  constructor(
      bytes: Uint8Array,
  ) {
    this.unpacker = BinaryUnpacker.fromBytes(bytes);
  }

  unpack(): SearchSnapshot {
    const version = u8.make(this.unpacker.unpackByte());

    switch(true) {
      case version.asNumber() === 1: {
        return this.unpackV1();
      } break;
      default: {
        throw new Error(`Unsupported search snapshot version {${version.asNumber()}}.`);
      } break;
    }
  }

  private unpackV1(): SearchSnapshot {
    const length = u16.make(this.unpacker.unpackLength());

    const elements: SnapshotElement[] = new Array(length.asNumber());
    for(let i = 0; i < length.asNumber(); i++) {
      elements[i] = this.unpackElement();
    }

    return SearchSnapshot.create({
      elements: elements,
    });
  }

  private unpackElement(): SnapshotElement {
    const typeId = u8.make(this.unpacker.unpackByte());

    switch(true) {
      case typeId.asNumber() === KeywordList.TYPE_ID.asNumber(): {
        return this.unpackKeywordList();
      } break;
      case typeId.asNumber() === EventList.TYPE_ID.asNumber(): {
        return this.unpackEventList();
      } break;
      case typeId.asNumber() === ArtistList.TYPE_ID.asNumber(): {
        return this.unpackArtistList();
      } break;
      case typeId.asNumber() === BrandList.TYPE_ID.asNumber(): {
        return this.unpackBrandList();
      } break;
      case typeId.asNumber() === OrganizerList.TYPE_ID.asNumber(): {
        return this.unpackOrganizerList();
      } break;
      default: {
        throw new Error(`Unsupported snapshot element type {${typeId.asNumber()}}.`);
      } break;
    }
  }

  private unpackKeywordList(): KeywordList {
    const listId = u8.make(this.unpacker.unpackByte());
    const length = u16.make(this.unpacker.unpackLength());

    const keywords: AsciiString120[] = new Array(length.asNumber());
    for(let i = 0; i < length.asNumber(); i++) {
      keywords[i] = AsciiString120.make(this.unpacker.unpackString());
    }

    return KeywordList.create({
      listId: listId,
      keywords: keywords,
    });
  }

  private unpackEventList(): EventList {
    const length = u16.make(this.unpacker.unpackLength());

    const events: Event[] = new Array(length.asNumber());
    for(let i = 0; i < length.asNumber(); i++) {
      events[i] = this.unpackEvent();
    }

    return EventList.create({
      events: events,
    });
  }

  private unpackEvent(): Event {
    const uniqueId = AsciiString20.make(this.unpacker.unpackString());
    const length = u16.make(this.unpacker.unpackLength());

    const keywords: KeywordRef[] = new Array(length.asNumber());
    for(let i = 0; i < length.asNumber(); i++) {
      keywords[i] = this.unpackKeywordRef();
    }

    return Event.create({
      uniqueId: uniqueId,
      keywords: keywords,
    });
  }

  private unpackArtistList(): ArtistList {
    const length = u16.make(this.unpacker.unpackLength());

    const artists: Artist[] = new Array(length.asNumber());
    for(let i = 0; i < length.asNumber(); i++) {
      artists[i] = this.unpackArtist();
    }

    return ArtistList.create({
      artists: artists,
    });
  }

  private unpackArtist(): Artist {
    const uniqueId = AsciiString20.make(this.unpacker.unpackString());
    const length = u16.make(this.unpacker.unpackLength());

    const keywords: KeywordRef[] = new Array(length.asNumber());
    for(let i = 0; i < length.asNumber(); i++) {
      keywords[i] = this.unpackKeywordRef();
    }

    return Artist.create({
      uniqueId: uniqueId,
      keywords: keywords,
    });
  }

  private unpackBrandList(): BrandList {
    const length = u16.make(this.unpacker.unpackLength());
    const brands: Brand[] = new Array(length.asNumber());
    for(let i = 0; i < length.asNumber(); i++) {
      brands[i] = this.unpackBrand();
    }

    return BrandList.create({
      brands: brands,
    });
  }

  private unpackBrand(): Brand {
    const uniqueId = AsciiString20.make(this.unpacker.unpackString());
    const length = u16.make(this.unpacker.unpackLength());

    const keywords: KeywordRef[] = new Array(length.asNumber());
    for(let i = 0; i < length.asNumber(); i++) {
      keywords[i] = this.unpackKeywordRef();
    }

    return Brand.create({
      uniqueId: uniqueId,
      keywords: keywords,
    });
  }

  private unpackOrganizerList(): OrganizerList {
    const length = u16.make(this.unpacker.unpackLength());
    const organizers: Organizer[] = new Array(length.asNumber());

    for(let i = 0; i < length.asNumber(); i++) {
      organizers[i] = this.unpackOrganizer();
    }

    return OrganizerList.create({
      organizers: organizers,
    });
  }

  private unpackOrganizer(): Organizer {
    const uniqueId = AsciiString20.make(this.unpacker.unpackString());
    const length = u16.make(this.unpacker.unpackLength());

    const keywords: KeywordRef[] = new Array(length.asNumber());
    for(let i = 0; i < length.asNumber(); i++) {
      keywords[i] = this.unpackKeywordRef();
    }

    return Organizer.create({
      uniqueId: uniqueId,
      keywords: keywords,
    });
  }

  private unpackKeywordRef(): KeywordRef {
    const listId = u8.make(this.unpacker.unpackByte());
    const index = u16.make(this.unpacker.unpackInt());

    return KeywordRef.create({
      listId: listId,
      index: index,
    });
  }
}
